/**
 * Users API service
 * Handles communication with the backend for users/gardeners
 */

import { apiGet, apiPost, apiDelete } from './api';

export type UserTimeOffEntry = {
	id: number;
	user_id: number;
	start_date: string;
	end_date: string;
	label: string | null;
	created_at?: string;
};

export interface UserResponse {
	id: number;
	email: string;
	name: string;
	role: 'seeker' | 'gardener' | 'company' | 'admin';
	admin_scope?: 'full' | 'insights' | null;
	gardener_type?: string;
	skills?: string[];
	company_name?: string;
	company_id?: number | null;
	active_company_id?: number | null;
	created_at: string;
	updated_at: string;
}

/**
 * Get all users (optionally filtered by role)
 */
export async function getUsers(params?: {
	role?: string | string[];
}): Promise<UserResponse[]> {
	const queryParams = new URLSearchParams();
	if (params?.role) {
		if (Array.isArray(params.role)) {
			params.role.forEach(role => queryParams.append('role', role));
		} else {
			queryParams.append('role', params.role);
		}
	}
	
	const queryString = queryParams.toString();
	const endpoint = `/users${queryString ? `?${queryString}` : ''}`;
	
	return apiGet<UserResponse[]>(endpoint);
}

/**
 * Get all gardeners (users with role 'gardener' or 'company')
 */
export async function getGardeners(): Promise<UserResponse[]> {
	return getUsers({ role: ['gardener', 'company'] });
}

const userIdParam = (userId: string) => `userId=${encodeURIComponent(userId)}`;

/**
 * List my time off (PTO) entries
 */
export async function getMyTimeOff(userId: string): Promise<UserTimeOffEntry[]> {
	return apiGet<UserTimeOffEntry[]>(`/users/me/time-off?${userIdParam(userId)}`);
}

/**
 * Add a time off entry
 */
export async function addTimeOff(
	userId: string,
	payload: { start_date: string; end_date: string; label?: string | null }
): Promise<UserTimeOffEntry> {
	return apiPost<UserTimeOffEntry>('/users/me/time-off', { userId, ...payload });
}

/**
 * Delete a time off entry
 */
export async function deleteTimeOff(userId: string, id: number): Promise<{ message: string }> {
	return apiDelete<{ message: string }>(`/users/me/time-off/${id}?${userIdParam(userId)}`);
}

export async function deleteMyAccount(userId: string, confirmEmail: string): Promise<{ message: string }> {
	return apiPost<{ message: string }>('/users/me/delete', { userId, confirmEmail });
}


